import { useContext, useEffect, useState } from 'react';
import { BraintreeCreditCardForm, type CreditCardError } from './BraintreeCreditCardForm';
import { Client, client } from 'braintree-web';
import { BraintreeApplePay } from './BraintreeApplePayForm';
import { FormControl, styled } from '@mui/material';
import { APPLE_PAY_VERSION } from '@/constants/applePay';
import { BindContext } from '@/contexts/bind/bind-context';
import { Controller, UseFormReturn } from 'react-hook-form';
import { encodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';
import { BraintreeInput } from '@/schemas/sceneDataSchema';
import useAnalytics from '@/hooks/use-analytics';
import { useSceneField } from '@/stores';

interface BraintreePaymentProps {
  client_token: string;
  exclude_applepay: boolean;
  legal: string;
  amount: string;
  form: UseFormReturn;
  component: BraintreeInput;
}

function isApplePaySupported() {
  if (window.location.protocol.startsWith('https') && window.ApplePaySession) {
    if (window.ApplePaySession.supportsVersion(APPLE_PAY_VERSION) && window.ApplePaySession.canMakePayments()) {
      return true;
    }
  }

  return false;
}

export function BraintreePayment({
  client_token,
  exclude_applepay,
  legal,
  amount,
  form,
  component,
}: BraintreePaymentProps) {
  const fieldKey = encodeFieldKey(component);
  const [clientInstance, setClientInstance] = useState<Client | null>(null);
  const storeField = useSceneField(fieldKey);
  const { trackEvent } = useAnalytics();
  const showApplePay = isApplePaySupported() && !exclude_applepay;
  const serverErrorMessage = storeField?.message;
  const hasServerError = storeField?.valid === false && typeof serverErrorMessage === 'string';
  const serverError: CreditCardError | null = hasServerError ? { message: serverErrorMessage, details: null } : null;

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { submitBind } = useContext(BindContext);

  async function submitForm(paymentMethodNonce: string) {
    form.setValue(fieldKey, paymentMethodNonce);
    trackEvent('BRAINTREE_PAYMENT_SUBMITTED');
    await submitBind();
  }

  useEffect(() => {
    if (storeField?.valid === false && storeField.message) {
      form.setError(fieldKey, { type: 'server', message: storeField.message });
      return;
    }

    form.clearErrors(fieldKey);
  }, [fieldKey, form, storeField?.message, storeField?.valid]);

  // Initialize Braintree client
  useEffect(() => {
    async function createClient() {
      const instance = await client.create({
        authorization: client_token,
      });
      void setClientInstance(instance);
    }
    void createClient();
  }, [client_token]);

  return (
    <Controller
      control={form.control}
      name={fieldKey}
      render={({ field, fieldState: { error } }) => (
        <FormControl
          fullWidth
          error={!!error}
        >
          {clientInstance && showApplePay && (
            <>
              <BraintreeApplePay
                onSuccess={submitForm}
                clientInstance={clientInstance}
                legal={legal}
                amount={amount}
                error={error}
                fieldProps={field}
              />
              <StyledDivider />
            </>
          )}

          {clientInstance && (
            <BraintreeCreditCardForm
              onSuccess={submitForm}
              clientInstance={clientInstance}
              legal={legal}
              amount={amount}
              serverError={serverError}
              fieldProps={field}
            />
          )}
        </FormControl>
      )}
    />
  );
}

const StyledDivider = styled('hr')`
  margin: 1rem 0;
  position: relative;
  display: block;
  width: 100%;
  border-color: ${({ theme }) => theme.palette.divider};
  border-style: solid;
  border-width: 1px 0 0 0;
  overflow: visible;

  &::after {
    content: 'or';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: ${({ theme }) => theme.palette.text.secondary};
    background-color: ${({ theme }) => theme.palette.background.paper};
    font-size: ${({ theme }) => theme.typography.body1.fontSize};
    width: 4.5rem;
    text-align: center;
  }
`;
